import * as React from 'react';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { formFields } from 'components/create-consignment/create-modal.constants';
import { isEqual, uniqWith } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Col,
    Form,
    FormInstance,
    Input,
    Popconfirm,
    message,
} from 'antd';
import { commonStyleSheet } from 'library/common-styles';
import { PickupAddress } from 'components/pickup/create-pickup/pickup.types';
import {
    BookOutlined,
    CheckCircleFilled,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleFilled,
} from '@ant-design/icons';
import Helper from 'library/Helper';
import Loader from 'components/common/Loader';
import CreateAddressForm from 'components/address/create-address';
import CreateAddressForm2 from 'components/address/create-address2';
import {
    deleteAddress,
    fetchPickupAddressMultiple,
    getDefaultAddress,
    setDefaultAddressData,
    editAddress as editBusinessAddress,
} from '../../../../network/pickup.api';
import { useDebounce } from 'hooks/use-debounce';
import { formRules } from 'library/constants';
import { Master } from 'types/master-data-types';
import Paginator from 'components/common/paginator';
import { NextOrPrev, Pagination } from 'library/Types';

const { useState } = React;

const styles = (theme: ThemeType) => ({
    addressMenu: {
        ...commonStyleSheet(theme).flexColumn,
        marginTop: 16,
        minWidth: 188,
        boxSizing: 'border-box',
        width: 1,
        borderRight: '1px solid #EDEDED',
        fontFamily: 'Open Sans',
        fontSize: 14,
        letterSpacing: 0,
        main: {
            '& .ant-radio-wrapper': {
                backgroundColor: 'blue',
            },
        },
    },
    addressItem: {
        ...commonStyleSheet(theme).flexRow,
        height: 42,
        width: 188,
        alignItems: 'center',
        cursor: 'pointer',
    },
    addressItemSelected: {
        ...commonStyleSheet(theme).flexRow,
        height: 42,
        width: 188,
        alignItems: 'center',
        cursor: 'pointer',
        fontWeight: 'bold',
        borderRight: `4px solid ${theme.secondryColor}`,
    },
    box: {
        ...commonStyleSheet(theme).flexRow,
        padding: 20,
        alignItems: 'normal',
        boxSizing: 'border-box',
        borderRadius: 4,
        backgroundColor: '#FFFFFF',
    },
    tabIcon: {
        height: 24,
        width: 24,
        borderRadius: 12,
        margin: '0px 10px',
    },
    filledCircle: {
        height: 20,
        width: 20,
        borderRadius: 10,
        border: '2px solid #FFF',
    },
    addressDetails: {
        ...commonStyleSheet(theme).flexRow,
        alignItems: 'top',
        width: '100%',
        marginTop: 16,
    },
    addressBox: {
        width: '100%',
        margin: '0 20px',
    },
    addressLineDetails: {
        ...commonStyleSheet(theme).flexRow,
        justifyContent: 'space-between',
        alignItems: 'top',
        width: '100%',
        padding: '0px 10px 0px 25px',
    },
    addressList: {
        ...commonStyleSheet(theme).flexColumn,
        overflow: 'auto',
    },
    addressLine: {
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        width: '98%',
        borderRadius: 4,
        backgroundColor: '#FCFCFC',
        margin: '4px 0px',
        cursor: 'pointer',
    },
    addressLineSelected: {
        backgroundColor: '#E7F5FF',
    },
    checkbox: {
        display: 'flex',
        color: '#111111',
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 0,
        padding: 12,
        borderRadius: 4,
    },
    addressName: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    searchAddress: {
        ...commonStyleSheet(theme).flexRow,
        justifyContent: 'flex-end',
        margin: 0,
    },
    line: {
        height: 1,
        margin: '12px 0px',
        width: '100%',
        backgroundColor: '#EDEDED',
    },
    formItem: {
        '& .ant-form-item': {
            marginBottom: 0,
        },
    },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, ReturnType<typeof mapStateToProps> {
    form: FormInstance;
    master: Master;
    setCreatingAddress: (value: boolean) => void;
    params: any;
}

const AddressField = (props: IProps) => {
    const { t, i18n } = useTranslation();
    const {
        classes,
        showAlternateAddress,
        uiTheme,
        form,
        showAddressCode,
        defaultMultipleLimit,
        allowDefaultSourceAddress,
        phoneRegex,
        countryWisePhoneRegex,
        master,
        autofillAddressFieldsBasedOnAddressHierarchy,
        setCreatingAddress,
        params,
    } = props;
    const { AddressTypes, ConsignmentCategory } = formFields;
    const [defaultAddress, setDefaultAddress] = useState<Record<any, any>>({});
    const { addressErrors, checkErrorOnAddressChange, disabledFields = {} } = params;
    const isAddressDisabled = (key: string) => !!disabledFields[key];

    const [addressList, setAddressList] = useState<Record<string, PickupAddress[]>>({
        srcAddress: [],
        dstAddress: [],
        alternateDelAddress1: [],
        alternateDelAddress2: [],
        redirectionAddress: [],
        billToDetails: [],
    });
    const [addressListVisited, setAddressListVisited] = useState<Record<string, boolean>>({
        srcAddress: true,
        dstAddress: false,
        alternateDelAddress1: false,
        alternateDelAddress2: false,
        redirectionAddress: false,
        billToDetails: false,
    });
    const [addressTypeVisited, setAddressTypeVisited] = useState<Record<string, boolean>>({
        srcAddress: true,
        dstAddress: false,
        alternateDelAddress1: false,
        alternateDelAddress2: false,
        redirectionAddress: false,
        billToDetails: false,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const selectedAddress: any = {
        srcAddress: Form.useWatch('srcAddress', form),
        dstAddress: Form.useWatch('dstAddress', form),
        alternateDelAddress1: Form.useWatch('alternateDelAddress1', form),
        alternateDelAddress2: Form.useWatch('alternateDelAddress2', form),
        redirectionAddress: Form.useWatch('redirectionAddress', form),
        billToDetails: Form.useWatch('billToDetails', form),
    };
    const [addressType, setAddressType] = React.useState<Record<string, string>>({
        srcAddress: 'saved',
        dstAddress: 'saved',
        alternateDelAddress1: 'saved',
        alternateDelAddress2: 'saved',
        redirectionAddress: 'saved',
        billToDetails: 'saved',
    });
    const [searchString, setSearchString] = useState<Record<string, string>>({
        srcAddress: '',
        dstAddress: '',
        alternateDelAddress1: '',
        alternateDelAddress2: '',
        redirectionAddress: '',
        billToDetails: '',
    });
    const [pagination, setPagination] = React.useState<Pagination>({
        nextOrPrev: 'first',
        currentPageNumber: 1,
        resultPerPage: 20,
        isNextPresent: false,
        lastUpdatedAt: null,
    });
    const [addressTypeRadio, setAddressTypeRadio] = React.useState<string>('srcAddress');
    const [editAddress, setEditAddress] = React.useState<PickupAddress | undefined>();
    const [toggle, setToggle] = React.useState<boolean>(true);
    const [billToSame, setBillToSame] = useState<boolean>(false);
    const [isDefaultLoaded, setIsDefaultLoaded] = useState(false);

    const loadDefaultAddress = async () => {
        if (!allowDefaultSourceAddress) return;
        setLoading(true);
        const response = await getDefaultAddress();
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setDefaultAddress(response.data);
        }
        setLoading(false);
    };

    const loadAddress = async (key: string) => {
        if (addressType[key] !== 'saved') {
            return;
        }
        const formData = form.getFieldsValue();
        let defaultAdd : Record<any, any> = {};
        if (allowDefaultSourceAddress && !isDefaultLoaded && key === 'srcAddress') {
            const response = await getDefaultAddress();
            if (response.isSuccess) {
                defaultAdd = response?.data || {};
                setDefaultAddress(defaultAdd);
                setIsDefaultLoaded(true);
            }
        } else {
            defaultAdd = defaultAddress;
        }
        let showDefaultBy = '';
        if (defaultMultipleLimit) {
            if (key === 'srcAddress') {
                showDefaultBy = 'src';
            } else {
                showDefaultBy = 'dst';
            }
        }
        setLoading(true);
        const response = await fetchPickupAddressMultiple({
            showDefaultBy,
            searchString: searchString[key] || '',
            parentCustomerId: master?.properties?.parent_address_book ? master?.Customer?.parent_id : undefined,
            pageNumber: pagination?.currentPageNumber,
            limit: 20,
        });
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        }
        let data = response?.data?.page_data || [];
        // eslint-disable-next-line no-underscore-dangle
        if (key === 'srcAddress' && allowDefaultSourceAddress && defaultAdd?._id
            && searchString[key].length < 1 && !defaultMultipleLimit) {
            let add: PickupAddress | undefined;
            data = await data.filter((item: PickupAddress) => {
                // eslint-disable-next-line no-underscore-dangle
                if (item.id === defaultAdd._id) {
                    add = item;
                    return false;
                }
                return true;
            });
            if (add) await data.unshift(add);
        }
        if (formData?.previousSelectedAddress && formData?.previousSelectedAddress[key]) {
            data.unshift(formData?.previousSelectedAddress[key]);
        }
        const isNextPresent = response?.data?.is_next_present || false;
        setPagination({
            ...pagination,
            isNextPresent,
        });
        setAddressList((oldAddressList) => {
            return {
                ...oldAddressList,
                [key]: uniqWith(data, isEqual),
            };
        });
        setLoading(false);
    };

    const debounceTimeSrc = useDebounce(searchString.srcAddress);
    React.useEffect(() => {
        if (addressTypeRadio === 'srcAddress') {
            loadAddress('srcAddress');
        }
    }, [debounceTimeSrc, addressType.srcAddress, toggle, addressTypeVisited.srcAddress]);

    const debounceTimeDst = useDebounce(searchString.dstAddress);
    React.useEffect(() => {
        if (addressTypeRadio === 'dstAddress') {
            loadAddress('dstAddress');
        }
    }, [debounceTimeDst, addressType.dstAddress, toggle, addressTypeVisited.dstAddress]);

    const debounceTimeAlt1 = useDebounce(searchString.alternateDelAddress1);
    React.useEffect(() => {
        if (addressTypeRadio === 'alternateDelAddress1') {
            loadAddress('alternateDelAddress1');
        }
    }, [debounceTimeAlt1, addressType.alternateDelAddress1, toggle, addressTypeVisited.alternateDelAddress1]);

    const complete = () => {
        return (
            <CheckCircleFilled
                style={{
                    color: '#27B479',
                    fontSize: 24,
                    margin: '0px 10px',
                }}
            />
        );
    };

    const inComplete = () => {
        return (
            <ExclamationCircleFilled
                style={{
                    color: '#EA2626',
                    fontSize: 24,
                    margin: '0px 10px',
                }}
            />
        );
    };

    const completeIncompleteIcon = (key: string) => {
        const active = key === addressTypeRadio;
        if (addressErrors[key] || addressErrors[`${key}Main`]) {
            return inComplete();
        }
        if (!addressListVisited[key] || active) {
            return (
                <div
                    className={classes.tabIcon}
                    style={{
                        border: active ? `2px solid ${uiTheme.primaryColor}`
                            : '2px solid #CCCCCC',
                        backgroundColor: active ? uiTheme.primaryColor : '#FFF',
                    }}
                >
                    <div className={classes.filledCircle} />
                </div>
            );
        }
        if (!selectedAddress[key]) {
            return inComplete();
        }
        return complete();
    };

    const consignmentCategory = Form.useWatch(ConsignmentCategory.key, form);
    const field = { ...AddressTypes };
    const showAlternate = showAlternateAddress && consignmentCategory === 'domestic';
    // TODO: check its config
    // field.options = field.options?.filter((option: any) => option.value !== 'redirectionAddress');
    if (!showAlternate) {
        field.options = field.options?.filter((option: any) => {
            return [
                'alternateDelAddress1',
                'alternateDelAddress2',
                'redirectionAddress',
            ].indexOf(option.value) === -1;
        });
    }
    if (consignmentCategory !== 'international') {
        field.options = field.options?.filter((option: any) => option.value !== 'billToDetails');
    }
    if (Array.isArray(field.options) && field.options.length > 0) {
        field.options.forEach((item: any) => {
            /* eslint-disable no-param-reassign */
            item.label = t(item.value);
        });
    }

    const prettyPrintedNumber = (num: string) => {
        return num;
    };

    const renderAddressDetails = (item: PickupAddress) => {
        const isRTL = uiTheme?.isRTL;
        const customerCode = window.localStorage.getItem('userCode');
        return (
            <div className={classes.addressLineDetails}>
                <div style={{ minWidth: '30%' }}>
                    {item.addressLine1 || ''}
                    {item.addressLine1 ? ', ' : ''}
                    {item.addressLine2 || ''}
                </div>
                <div style={{
                    minWidth: '30%',
                    marginLeft: isRTL ? undefined : '10%',
                    marginRight: isRTL ? '10%' : undefined,
                }}
                >
                    {item.pincode || ''}
                    {item.pincode && item.cityName ? ' - ' : ''}
                    {item.cityName || ''}
                    <br />
                    {item.stateName || ''}
                    {item.stateName ? ', ' : ''}
                    {item.countryName || ''}
                </div>
                <div style={{ minWidth: '30%', textAlign: 'right' }}>
                    {item.phone
                        ? (
                            <>
                                {prettyPrintedNumber(item.phone)}
                                <br />
                            </>
                        ) : null}
                    {item.alternatePhone
                        ? (
                            <>
                                {prettyPrintedNumber(item.alternatePhone)}
                                <br />
                            </>
                        ) : null}
                    {item.alternatePhone2
                        ? (
                            <>
                                {prettyPrintedNumber(item.alternatePhone2)}
                                <br />
                            </>
                        ) : null}
                    {item.notes
                        ? (
                            <>
                                {item.notes}
                                <br />
                            </>
                        ) : null}
                    {item.email ? item.email : null}
                    <b>{`${item.locationId || ''}`}</b>
                    <br />
                    <b>{item.locationId ? item.locationCode || `${customerCode}_${item.locationId}` : ''}</b>
                </div>
            </div>
        );
    };

    const renderParentAddress = (isParent: boolean) => {
        return (
            <span
                style={{
                    marginRight: '15px',
                    fontSize: 'small',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    border: isParent ? '1px solid' : 'none',
                }}
            >
                {isParent ? 'Parent' : ''}
            </span>
        );
    };

    const renderEdit = (item: PickupAddress, key: string, isEnabled?: boolean) => {
        return (
            <EditOutlined
                onClick={(e) => {
                    if (isEnabled) {
                        Helper.stops(e as any);
                        setAddressType({
                            ...addressType,
                            [key]: 'createnew',
                        });
                        setEditAddress(item);
                        setCreatingAddress(true);
                    }
                }}
                style={{ color: isEnabled ? uiTheme.primaryColor : 'lightgray', marginRight: '8px' }}
            />
        );
    };

    const saveDefaultAddress = async (address: PickupAddress, isMultipleDefault: boolean = false) => {
        setLoading(true);
        const customerCode = window.localStorage.getItem('userCode') || '';
        if (isMultipleDefault) {
            const response = await editBusinessAddress({ customerCode, ...address });
            if (!response.isSuccess) {
                message.error(response.errorMessage);
            } else {
                loadAddress(addressTypeRadio);
            }
        } else {
            const id = address.id;
            const response = await setDefaultAddressData({
                businessAddressId: id,
            });
            if (!response.isSuccess) {
                message.error(response.errorMessage);
            } else {
                loadDefaultAddress();
                setLoading(true);
                const key = 'srcAddress';
                if (addressTypeRadio === key) {
                    let data = addressList[key] || [];
                    // eslint-disable-next-line no-underscore-dangle
                    if (allowDefaultSourceAddress && id && searchString[key].length < 1) {
                        let add: PickupAddress | undefined;
                        data = data.filter((item: PickupAddress) => {
                            // eslint-disable-next-line no-underscore-dangle
                            if (item.id === id) {
                                add = item;
                                return false;
                            }
                            return true;
                        });
                        if (add) data.unshift(add);
                        setAddressList((oldAddressList) => {
                            return {
                                ...oldAddressList,
                                [key]: data,
                            };
                        });
                    }
                }
            }
        }
        setLoading(false);
    };

    const renderMarkAsDefaultMultiple = (item: PickupAddress) => {
        const isMultipleDefault = addressTypeRadio === 'srcAddress'
            ? item?.isDefaultSrc : item?.isDefaultDst;
        return (
            <>
                { isMultipleDefault ? (
                    <Button
                        style={{ cursor: 'default' }}
                        shape="round"
                    >
                        Default
                    </Button>
                ) : null}
                { isMultipleDefault ? (
                    <Popconfirm
                        placement="topLeft"
                        title="Are you sure you want to remove this default Address"
                        onConfirm={(e) => {
                            Helper.stops(e as any);
                            saveDefaultAddress({
                                ...item,
                                isDefaultSrc: addressTypeRadio === 'srcAddress' ? false : item?.isDefaultSrc,
                                isDefaultDst: addressTypeRadio !== 'srcAddress' ? false : item?.isDefaultDst,
                            }, true);
                        }}
                        onCancel={(e) => Helper.stops(e as any)}
                    >
                        <BookOutlined
                            style={{ color: 'red' }}
                            onClick={(e) => { Helper.stops(e as any); }}
                        />
                    </Popconfirm>
                ) : (
                    <BookOutlined
                        onClick={(e) => {
                            Helper.stops(e as any);
                            saveDefaultAddress({
                                ...item,
                                isDefaultSrc: addressTypeRadio === 'srcAddress' ? true : item?.isDefaultSrc,
                                isDefaultDst: addressTypeRadio !== 'srcAddress' ? true : item?.isDefaultDst,
                            }, true);
                        }}
                        style={{ color: uiTheme.primaryColor, marginRight: 8 }}
                    />
                )}
            </>
        );
    };

    const renderMarkAsDefault = (item: PickupAddress) => {
        if (defaultMultipleLimit) {
            return renderMarkAsDefaultMultiple(item);
        }
        // eslint-disable-next-line no-underscore-dangle
        if (defaultAddress._id === item.id) {
            return (
                <Button
                    style={{ cursor: 'default' }}
                    shape="round"
                >
                    Default
                </Button>
            );
        }
        return (
            <BookOutlined
                onClick={(e) => {
                    Helper.stops(e as any);
                    saveDefaultAddress(item);
                }}
                style={{ color: uiTheme.primaryColor, marginRight: 8 }}
            />
        );
    };
    const showaddcode = (item: PickupAddress) => {
        return (
            <span style={{ display: 'flex', fontWeight: 'bold' }}>
                <span>{item?.addressCode}</span>
                <span style={{ margin: '0px 6px 0px 4px', fontWeight: 'bold' }}> | </span>
            </span>
        );
    };

    const handleDelete = async (e: any, item: PickupAddress) => {
        const customerCode = window.localStorage.getItem('userCode') || '';
        Helper.stops(e);
        const result = await deleteAddress({ id: item.id, addressCode: item.addressCode, customerCode });
        if (result.isSuccess) {
            message.success('Address Deleted Successfully');
            setToggle(!toggle);
        } else {
            message.error(result.errorMessage);
        }
    };

    const renderDelete = (item: PickupAddress, isEnabled?: boolean) => {
        return (isEnabled ? (
            <Popconfirm
                placement="topLeft"
                title={t('delete_address_text')}
                onConfirm={(e) => handleDelete(e, item)}
                okText={t('ok_text')}
                cancelText={t('cancel')}
                onCancel={(e) => Helper.stops(e as any)}
            >
                <DeleteOutlined
                    onClick={(e) => {
                        Helper.stops(e as any);
                    }}
                    style={{ color: 'red' }}
                />
            </Popconfirm>
        ) : (
            <DeleteOutlined
                style={{
                    color: 'lightgray',
                }}
            />
        ));
    };

    const isCurrentAddressSelected = (key: string, item: PickupAddress) => {
        return Boolean(selectedAddress && selectedAddress[key] && item.id === selectedAddress[key]?.id);
    };

    const handleAddressChange = (e: any, key: string, item: PickupAddress) => {
        if (isAddressDisabled(key)) {
            Helper.stops(e as any);
            return;
        }
        const isCurrentSelected = isCurrentAddressSelected(key, item);
        if (addressTypeRadio === 'billToDetails') {
            setBillToSame(false);
        }
        Helper.stops(e as any);
        // remove selection if clicked on same row
        if (isCurrentSelected) {
            form.setFieldsValue({
                [key]: null,
            });
        } else {
            // save address
            form.setFieldsValue({
                [key]: null,
            });
            form.setFieldsValue({
                [key]: item,
            });
        }
        checkErrorOnAddressChange(addressTypeRadio);
    };

    const renderCheckbox = (item: PickupAddress, key: string) => {
        const showEditButtons = !['oldDstAddress', 'oldSrcAddress'].includes(item?.id);
        const isEditEnabled = (item?.clientId === master?.Customer?.id) && !master?.properties?.disable_address_book_edit;
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
            }}
            >
                <Checkbox
                    name={`${key}-checkbox`}
                    key={item.id}
                    checked={isCurrentAddressSelected(key, item)}
                    className={classes.checkbox}
                    disabled={isAddressDisabled(key)}
                    onChange={(e) => handleAddressChange(e, key, item)}
                >
                    <div className={classes.addressName}>
                        <span style={{ display: 'flex', flex: 1 }}>
                            {showAddressCode && item?.addressCode ? showaddcode(item) : null}
                            <span style={{ display: 'inline-block', fontWeight: 'bold' }}>
                                {item.name || ''}
                            </span>
                        </span>
                    </div>
                </Checkbox>
                {
                    showEditButtons && (
                        <div style={{ justifyContent: 'flex-end' }}>
                            {allowDefaultSourceAddress ? renderMarkAsDefault(item) : null}
                            {renderParentAddress(!isEditEnabled)}
                            {renderEdit(item, key, isEditEnabled)}
                            {renderDelete(item, isEditEnabled)}
                        </div>
                    )
                }
            </div>
        );
    };

    const renderErrorMessages = (item: PickupAddress, key: string) => {
        const error = addressErrors[key];
        const isCurrentSelected = isCurrentAddressSelected(key, item);
        if (error && isCurrentSelected) {
            return (
                <div style={{ color: 'red', fontSize: 12, paddingBottom: 8 }}>
                    {error}
                </div>
            );
        }
        return null;
    };

    const renderAddressLine = (item: PickupAddress, key: string) => {
        let className = classes.addressLine;
        const isCurrentSelected = isCurrentAddressSelected(key, item);
        if (isCurrentSelected) {
            className = [classes.addressLineSelected, classes.addressLine].join(' ');
        }
        return (
            <>
                <div
                    key={item.id}
                    onClick={(e) => handleAddressChange(e, key, item)}
                    className={className}
                >
                    {renderCheckbox(item, key)}
                    {renderAddressDetails(item)}
                </div>
                {renderErrorMessages(item, key)}
            </>
        );
    };

    const formRuleRequired = {
        ...formRules.required,
        message: i18n.exists('required') ? t('required') : 'Required',
    };

    const renderAddressList = (key: string) => {
        if (loading) {
            return <Loader zIndex={10} />;
        }
        return (
            <Form.Item
                name={key}
                className={classes.formItem}
                rules={['srcAddress', 'dstAddress'].indexOf(key) > -1 ? [formRuleRequired] : []}
            >
                <div className={classes.addressList}>
                    {addressList[key].map((item: PickupAddress) => renderAddressLine(item, key))}
                </div>
            </Form.Item>
        );
    };

    const renderCreateAddressButton = (key: string) => {
        if (isAddressDisabled(key)) return null;
        return (master?.properties?.disable_address_book_edit
            ? null : (
                <>
                    <Button
                        type="link"
                        onClick={() => {
                            setAddressType({
                                ...addressType,
                                [key]: 'createnew',
                            });
                            setEditAddress(undefined);
                            setCreatingAddress(true);
                        }}
                    >
                        +
                        {t('add_new_address')}
                    </Button>
                </>
            )
        );
    };

    const renderSelectDefaultAddress = (key: string) => {
        // eslint-disable-next-line no-underscore-dangle
        if (!defaultAddress?._id) return null;
        if (defaultMultipleLimit) return null;
        if (isAddressDisabled(key)) return null;
        return (
            <Button
                type="link"
                onClick={() => {
                    // eslint-disable-next-line no-underscore-dangle
                    const item: PickupAddress | undefined = addressList[key].find(
                        // eslint-disable-next-line no-underscore-dangle
                        (add) => add.id === defaultAddress?._id,
                    );
                    if (item) {
                        form.setFieldsValue({
                            [key]: item,
                        });
                    }
                }}
            >
                {t('select_default_address')}
            </Button>
        );
    };

    const handlePagination = (nextOrPrev: NextOrPrev) => {
        const { currentPageNumber } = pagination;
        let newPageNumber = currentPageNumber;
        switch (nextOrPrev) {
            case 'first':
                newPageNumber = 1;
                break;
            case 'next':
                newPageNumber = currentPageNumber + 1;
                break;
            case 'prev':
                newPageNumber = currentPageNumber - 1;
                break;
            default:
                break;
        }
        const newPagination: Pagination = {
            ...pagination,
            nextOrPrev,
            currentPageNumber: newPageNumber,
        };
        setPagination(newPagination as Pagination);
        setToggle(!toggle);
    };

    const renderPaginator = () => {
        return (
            <Paginator
                currentPageNumber={pagination?.currentPageNumber}
                isNextPresent={pagination?.isNextPresent}
                onNextClick={() => handlePagination('next')}
                onPrevClick={() => handlePagination('prev')}
                onFirstClick={() => handlePagination('first')}
            />

        );
    };

    const renderAddressSearch = (key: string) => {
        return (
            <div className={classes.searchAddress}>
                { addressTypeRadio === 'billToDetails'
                    ? (
                        <Checkbox
                            checked={billToSame}
                            onChange={(e) => {
                                setBillToSame(e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldsValue({
                                        billToDetails: selectedAddress.dstAddress,
                                    });
                                }
                            }}
                            style={{
                                marginLeft: 8,
                                marginRight: 'auto',
                            }}
                        >
                            {t('use_same_as_delivery_address')}
                        </Checkbox>
                    )
                    : null}
                {renderPaginator()}
                {renderCreateAddressButton(key)}
                {allowDefaultSourceAddress ? renderSelectDefaultAddress(key) : null}
                <Input
                    placeholder={t('search_for_addresses')}
                    type="search"
                    onChange={(e) => setSearchString({
                        ...searchString,
                        [key]: e.target.value,
                    })}
                    style={{ width: 200 }}
                />
            </div>
        );
    };

    const renderLine = () => {
        return (
            <div className={classes.line} />
        );
    };

    const renderExisting = (key: string) => {
        return (
            <>
                {renderAddressSearch(key)}
                {renderLine()}
                {renderAddressList(key)}
                {renderLine()}
            </>
        );
    };

    const updateFormData = (address: any, key: string) => {
        form.setFieldsValue({
            [key]: null,
        });
        form.setFieldsValue({
            [key]: address,
        });
        checkErrorOnAddressChange(addressTypeRadio);
    };

    const renderAddressForm = (key: string) => {
        const createNew = addressType[key] === 'createnew';
        const newAddressComp = createNew ? (
            <div>
                {autofillAddressFieldsBasedOnAddressHierarchy
                    ? (
                        <CreateAddressForm2
                            editData={editAddress}
                            page="Address"
                            onClose={() => {
                                setAddressType({
                                    ...addressType,
                                    [key]: 'saved',
                                });
                                setEditAddress(undefined);
                                setCreatingAddress(false);
                            }}
                            phoneRegex={phoneRegex}
                            isInternationalAddress={
                                consignmentCategory === 'international' && addressTypeRadio !== 'srcAddress'
                                // || (editAddress?.isInternational || false)
                            }
                            countryWisePhoneRegex={countryWisePhoneRegex}
                            showBackButton
                            updateFormData={(address: any) => updateFormData(address, key)}
                        />
                    ) : (
                        <CreateAddressForm
                            editData={editAddress}
                            page="Address"
                            onClose={() => {
                                setAddressType({
                                    ...addressType,
                                    [key]: 'saved',
                                });
                                setEditAddress(undefined);
                                setCreatingAddress(false);
                            }}
                            phoneRegex={phoneRegex}
                            isInternationalAddress={
                                consignmentCategory === 'international' && addressTypeRadio !== 'srcAddress'
                                // || (editAddress?.isInternational || false)
                            }
                            countryWisePhoneRegex={countryWisePhoneRegex}
                            showBackButton
                            updateFormData={(address: any) => updateFormData(address, key)}
                        />
                    )}
            </div>
        ) : <></>;
        const addressForm = (
            <div style={{ display: createNew ? 'none' : 'block' }}>
                {renderExisting(key)}
            </div>
        );
        return (
            <>
                {newAddressComp}
                {addressForm}
            </>
        );
    };

    return (
        <Col span={24}>
            <div className={classes.box}>
                <div className={classes.addressMenu}>
                    {field.options?.map((address: any) => {
                        return (
                            <div
                                key={address.value}
                                onClick={() => {
                                    setAddressTypeRadio(address.value);
                                    setAddressTypeVisited({ ...addressTypeVisited, [address.value]: true });
                                    setAddressListVisited({
                                        ...addressListVisited,
                                        [address.value]: true,
                                    });
                                    checkErrorOnAddressChange(address.value);
                                }}
                                className={
                                    address.value === addressTypeRadio
                                        ? classes.addressItemSelected : classes.addressItem
                                }
                            >
                                {completeIncompleteIcon(address.value)}
                                {address.label}
                            </div>
                        );
                    })}
                </div>
                <div className={classes.addressDetails}>
                    {field.options?.map((address: any) => {
                        const isVisible = address.value === addressTypeRadio;
                        return (
                            <div
                                className={classes.addressBox}
                                style={{
                                    display: isVisible ? 'block' : 'none',
                                    height: '500px',
                                    overflowY: 'scroll',
                                }}
                            >
                                {renderAddressForm(address.value)}
                            </div>
                        );
                    })}
                </div>
            </div>
        </Col>
    );
};

const mapStateToProps = (state: any) => {
    const { master, uiTheme } = state;
    const { config, parts_to_show } = master;
    const customerPortalConfig = config?.customer_portal_config;
    const showAlternateAddress = parts_to_show?.allow_multiple_alternate_address_in_fm_api;
    const showAddressCode = config?.customer_portal_config?.allow_address_code_in_addresses;
    const disableDefaultAddress = !customerPortalConfig?.disable_default_address;
    const defaultAddressLimit = customerPortalConfig?.default_addresses_limit;
    const allowDefaultSourceAddress = disableDefaultAddress && defaultAddressLimit;
    const defaultMultipleLimit = config?.customer_portal_config?.default_addresses_limit;
    const phoneRegex = parts_to_show?.remove_regex ? null : config?.phone_regex;
    const countryWisePhoneRegex = config?.country_wise_phone_regex || [];
    const autofillAddressFieldsBasedOnAddressHierarchy = parts_to_show?.autofill_address_fields_based_on_address_hierarchy;
    return {
        showAlternateAddress,
        uiTheme,
        showAddressCode,
        allowDefaultSourceAddress,
        defaultMultipleLimit,
        phoneRegex,
        countryWisePhoneRegex,
        autofillAddressFieldsBasedOnAddressHierarchy,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
};

export default GenericHoc(hocConfig)(AddressField);
